import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Collapse
} from '@mui/material';
import { Visibility, Edit, Delete, ExpandLess, ExpandMore } from '@mui/icons-material';
import api from '../api/axios';
import toast from 'react-hot-toast';

// Three‑level sidebar structure
const sidebarStructure = [
  {
    name: 'Data Analysis',
    children: [
      {
        name: 'Data Quality',
        children: [
          { name: 'Missing Rate', modules: ['missing_rate'] },
          { name: 'Missing Imputation', modules: ['missing_imputation'] }
        ]
      },
      {
        name: 'Discretization',
        children: [
          { name: 'Expert Cut', modules: ['expert_cut'] },
          { name: 'Isotonic Regression', modules: ['isotonic_regression'] },
          { name: 'Monotonic Light GBM', modules: ['monotonic_lightgbm'] },
          { name: 'Monotonic Optimal Binning', modules: ['monotonic_optimal_binning'] },
          { name: 'Quantile Cut', modules: ['quantile_cut'] }
        ]
      },
      {
        name: 'Extremes values',
        children: [
          { name: 'IQR', modules: ['iqr'] },
          { name: 'Isolation Forest', modules: ['isolation_forest'] },
          { name: 'ZScore', modules: ['zscore'] }
        ]
      }
    ]
  },
  {
    name: 'Risk Diff',
    children: [
      { name: 'Sampling', modules: ['sampling'] },
      { name: 'CART', modules: ['cart'] },
      { name: 'Performance Testing', modules: ['performance_testing'] }
    ]
  },
  {
    name: 'Max Recovery Period',
    children: [
      { name: 'Cash flows', modules: ['mrp_cashflows'] },
      { name: 'Numbers', modules: ['mrp_numbers'] }
    ]
  },
  {
    name: 'CF Projection',
    children: [
      { name: 'Additive Model', modules: ['additive_projection'] },
      { name: 'Multiplicative Model', modules: ['multiplicative_projection'] }
    ]
  },
  {
    name: 'Risk quantification',
    children: [
      { name: 'LRA', modules: ['lra'] },
      { name: 'MoC C', modules: ['moc_c'] },
      {
        name: 'MoC downturn',
        children: [
          { name: 'Dwt Identification', modules: ['dwt_identification'] },
          { name: 'Dwt Quantification', modules: ['dwt_quantification'] }
        ]
      }
    ]
  }
];

const ProjectAnalyses = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});

  useEffect(() => {
    if (projectId) {
      fetchAnalyses();
    }
  }, [projectId]);

  const fetchAnalyses = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/analyses?project_id=${projectId}`);
      setAnalyses(Array.isArray(res.data) ? res.data : []);
      
      // Auto‑expand nodes that contain analyses
      const initialExpand = {};
      
      const checkAndExpand = (node) => {
        if (node.modules) {
          const hasAnalyses = (analyses || []).some(a => node.modules.includes(a.module_name));
          if (hasAnalyses) {
            // Mark this node to be expanded, and also its parent chain
            return true;
          }
          return false;
        }
        if (Array.isArray(node.children)) {
          const anyChildHas = node.children.some(child => checkAndExpand(child));
          if (anyChildHas) {
            initialExpand[node.name] = true;
            return true;
          }
          return false;
        }
        return false;
      };
      
      sidebarStructure.forEach(node => checkAndExpand(node));
      setExpandedNodes(initialExpand);
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to load analyses');
    } finally {
      setLoading(false);
    }
  };

  const toggleNode = (name) => {
    setExpandedNodes(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'draft': return 'default';
      case 'pending': return 'warning';
      case 'completed': return 'success';
      case 'validated': return 'info';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const handleConsult = (analysis) => {
    const modulePath = analysis.module_name.replace(/_/g, '-');
    navigate(`/${modulePath}?analysis_id=${analysis.id}&project_id=${projectId}&mode=view`);
  };

  const handleModify = (analysis) => {
    const modulePath = analysis.module_name.replace(/_/g, '-');
    navigate(`/${modulePath}?analysis_id=${analysis.id}&project_id=${projectId}&mode=edit`);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/analyses/${deleteId}`);
      toast.success('Analysis deleted');
      fetchAnalyses();
    } catch (err) {
      toast.error('Failed to delete');
    } finally {
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  const renderAnalysesTable = (modules) => {
    const filtered = analyses.filter(a => modules.includes(a.module_name));
    if (filtered.length === 0) return null;
    
    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: '#34495e' }}>
              <TableCell sx={{ color: 'white', fontWeight: 500, fontSize: '12px' }}>Dataset</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 500, fontSize: '12px' }}>Parameters</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 500, fontSize: '12px' }}>Description</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 500, fontSize: '12px' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 500, fontSize: '12px' }}>Created</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 500, fontSize: '12px' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((a) => (
              <TableRow key={a.id} hover>
                <TableCell sx={{ fontSize: '12px' }}>
                  {a.input_parameters?.dataset_name || a.input_parameters?.dataset_id || '-'}
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontSize: '11px', 
                    maxWidth: '200px', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    cursor: 'pointer'
                  }}
                  title={JSON.stringify(a.input_parameters, null, 2)}
                >
                  {a.input_parameters ? Object.keys(a.input_parameters).join(', ') : '-'}
                </TableCell>
                <TableCell sx={{ fontSize: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.description || '-'}
                </TableCell>
                <TableCell>
                  <Chip label={a.status} size="small" color={getStatusColor(a.status)} />
                </TableCell>
                <TableCell sx={{ fontSize: '12px' }}>{new Date(a.created_at).toLocaleString()}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton onClick={() => handleConsult(a)} size="small">
                      <Visibility fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => handleModify(a)} size="small">
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => { setDeleteId(a.id); setDeleteDialogOpen(true); }} size="small" color="error">
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderNode = (node, level = 0) => {
    const hasContent = (n) => {
      if (n.modules && n.modules.length > 0) {
        return (analyses || []).some(a => n.modules.includes(a.module_name));
      }
      if (Array.isArray(n.children) && n.children.length > 0) {
        return n.children.some(child => hasContent(child));
      }
      return false;
    };
    
    if (!hasContent(node)) return null;
    
    const isExpanded = expandedNodes[node.name];
    const marginLeft = level * 20;
    
    // Leaf node (has modules)
    if (node.modules && node.modules.length > 0) {
      return (
        <div key={node.name} style={{ marginLeft: `${marginLeft + 20}px` }}>
          <div 
            style={{ 
              background: '#e8f8f5', 
              padding: '8px 16px', 
              fontWeight: 500, 
              fontSize: '13px', 
              color: '#1abc9c', 
              cursor: 'pointer', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              borderBottom: '1px solid #e0e0e0',
              borderRadius: '4px',
              marginBottom: '4px'
            }} 
            onClick={() => toggleNode(node.name)}
          >
            <span>{node.name}</span>
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </div>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <div style={{ padding: '8px 0 16px 0' }}>
              {renderAnalysesTable(node.modules)}
            </div>
          </Collapse>
        </div>
      );
    }
    
    // Parent node (has children)
    return (
      <div key={node.name} style={{ marginLeft: `${marginLeft}px` }}>
        <div 
          style={{ 
            background: '#f5f7fa', 
            padding: '10px 16px', 
            fontWeight: 600, 
            fontSize: '14px', 
            color: '#2c3e50', 
            cursor: 'pointer', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            marginBottom: '8px'
          }} 
          onClick={() => toggleNode(node.name)}
        >
          <span>{node.name}</span>
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </div>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <div style={{ paddingLeft: '16px', marginBottom: '16px' }}>
            {node.children.map(child => renderNode(child, level + 1))}
          </div>
        </Collapse>
      </div>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (analyses.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Analyses</Typography>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">No analyses saved yet. Run an analysis and click "Save" to store it.</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Analyses</Typography>
      {sidebarStructure.map(node => renderNode(node))}
      
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Analysis</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this analysis? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectAnalyses;